<template>
  <div class="flex flex-col gap-2">
    <!-- File Upload Dropzone -->
    <AwFileUpload
      ref="awFileUploadRef"
      :files="[]"
      :accept="adapter.acceptedMimeTypes"
      :max-files="adapter.maxFiles"
      :disabled="disabled"
      @update:files="handleNewFiles"
      @rejected="onRejected"
    />

    <!-- Verification Dialog -->
    <DocumentVerificationDialog
      ref="verificationDialogRef"
      v-model:open="showVerificationDialog"
      :title="adapter.documentName"
      :files="files"
      :max-files="adapter.maxFiles"
      :form-fields="adapter.formFields"
      :adapter="adapter"
      :initial-data="extractedData"
      :is-loading="isExtracting"
      :is-storing="isStoring"
      :extraction-error="extractionError"
      :file-limit-warning="fileLimitWarning"
      :file-mime-type-warning="fileMimeTypeWarning"
      :context="context"
      @complete="handleVerificationComplete"
      @cancel="resetFlow"
      @request-close="handleVerificationRequestClose"
      @add-files-requested="awFileUploadRef?.openFileDialog()"
      @remove-file="handleRemoveFile"
      @dismiss-file-limit-warning="dismissFileLimitWarning"
      @dismiss-file-mime-type-warning="dismissFileMimeTypeWarning"
    />

    <!-- Confirmation Dialog -->
    <AwModal v-model:open="showConfirmDialog" title="Es-tu sûr de vouloir abandonner l'ajout du document ?" size="md">
      <template #body>
        <div class="p-4">Les informations saisies seront perdues.</div>
      </template>

      <template #footer>
        <AwButton color="secondary" variant="outline" @click="handleCancelClose">Non, continuer</AwButton>
        <AwButton color="secondary" @click="handleConfirmClose">Oui, abandonner</AwButton>
      </template>
    </AwModal>
  </div>
</template>

<script setup lang="ts">
import { AwFileUpload } from "@asap/design";
import type { RejectedFile } from "@asap/design";
import { getHandler } from "../handlers/registry";
import type { AnyDocumentTypeHandler } from "../handlers/handler";
import type { DocumentType } from "../handlers/types";
import { useDocumentUpload } from "./useDocumentUpload";
import DocumentVerificationDialog from "./DocumentVerificationDialog.vue";

interface Props {
  documentType: DocumentType;
  context: Record<string, unknown>;
  disabled?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  uploaded: [documentId: string];
}>();

// AwFileUpload template ref for programmatic file dialog
const awFileUploadRef = useTemplateRef("awFileUploadRef");

// Get handler for the document type
const adapter = computed<AnyDocumentTypeHandler>(() => getHandler(props.documentType));

// Context as computed ref for composable
const contextRef = computed(() => props.context);

// Use the composable
const {
  files,
  fileLimitWarning,
  fileMimeTypeWarning,
  dismissFileLimitWarning,
  dismissFileMimeTypeWarning,
  extractedData,
  extractionError,
  isExtracting,
  showVerificationDialog,
  verificationDialogRef,
  isStoring,
  handleNewFiles,
  handleRemoveFile,
  handleVerificationComplete,
  handleVerificationRequestClose,
  showConfirmDialog,
  handleConfirmClose,
  handleCancelClose,
  resetFlow,
} = useDocumentUpload({
  adapter,
  context: contextRef,
  onUploaded: (documentId) => emit("uploaded", documentId),
});

// Surface type rejections from AwFileUpload as mime type warnings
function onRejected(rejected: RejectedFile[]) {
  const typeRejections = rejected.filter((r) => r.reason === "type");
  if (typeRejections.length > 0) {
    const names = typeRejections.map((r) => r.file.name).join(", ");
    fileMimeTypeWarning.value = `Fichier(s) non autorisé(s) ignoré(s): ${names}.`;
  }
}
</script>
